import React, { useEffect, useState } from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Link } from "react-router-dom";

export default function CardList() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/cards`);
      setCards(res.data);
    } catch (error) {
      console.error("Error fetching cards:", error);
      setMessage("Failed to load cards.");
    } finally {
      setLoading(false);
    }
  };

  const deleteCard = async (id) => {
    if (!window.confirm("Are you sure you want to delete this card?")) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/cards/${id}`);
      setMessage("Card deleted successfully.");
      setCards(cards.filter((card) => card.id !== id));
    } catch (error) {
      console.error("Error deleting card:", error);
      setMessage("Failed to delete card.");
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index) return;

    const reorderedCards = Array.from(cards);
    const [movedCard] = reorderedCards.splice(source.index, 1);
    reorderedCards.splice(destination.index, 0, movedCard);

    const updatedCards = reorderedCards.map((card, index) => ({
      ...card,
      position: index + 1,
    }));

    try {
      for (let i = 0; i < updatedCards.length; i++) {
        const card = updatedCards[i];
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/cards/${card.id}`,
          card,
        );
      }

      setCards(updatedCards);
      setMessage("Cards reordered successfully.");
    } catch (error) {
      console.error("Error updating card positions:", error);
      setMessage("Failed to reorder cards.");
    }
  };

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-start mb-4">
        <h3 className="mb-0 me-3">Cards</h3>
        <Link
          to="/xl-admin/add-card"
          className="btn btn-inchworm btn-sm"
          role="button"
          style={{
            padding: "0.25rem 0.4rem",
            fontSize: "0.8rem",
            lineHeight: "1",
          }}
        >
          Add New
        </Link>
      </div>

      {/* Message */}
      {message && <div className="alert alert-info">{message}</div>}

      {/* Cards Table */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="cards" direction="vertical">
            {(provided) => (
              <div
                className="table-responsive"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <table className="table table-bordered table-striped">
                  <thead className="table-success">
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>URL</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.length > 0 ? (
                      cards.map((card, index) => (
                        <Draggable
                          key={card.id}
                          draggableId={String(card.id)}
                          index={index}
                        >
                          {(provided) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <td>{card.id}</td>
                              <td>{card.title}</td>
                              <td>{card.description}</td>
                              <td>
                                <a
                                  href={card.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {card.url}
                                </a>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Link
                                    to={`/xl-admin/edit-card?id=${card.id}`}
                                    className="btn btn-sm btn-warning"
                                  >
                                    Edit
                                  </Link>
                                  <button
                                    onClick={() => deleteCard(card.id)}
                                    className="btn btn-sm btn-danger"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No cards found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}
